"use client";

import { useMemo, useState, useEffect } from "react";
import { type StreakResponseV2, AUTOMATIC_STREAK_STATUS } from "@cede/types";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useExecutingCycle } from "@/hooks/automatic-streak";
import { cn } from "@/lib/utils";
import {
  getStatusConfig,
  getStopReasonLabel,
  isStreakActive,
  isStreakCancellable,
  formatStreakDate,
  formatCountdown,
  formatTimeframe,
} from "@/utils/automatic-streak";
import {
  CheckCircle,
  Pause,
  Ban,
  AlertTriangle,
  Loader2,
  CalendarClock,
  ShieldCheck,
  Info,
  Target,
} from "lucide-react";

/**
 * Get the epoch start time for a V2 streak (the scheduledStartTime)
 */
const getEpochStartTime = (streak: StreakResponseV2): Date | null => {
  // Don't show countdown for terminal states
  if (
    streak.status === AUTOMATIC_STREAK_STATUS.COMPLETED ||
    streak.status === AUTOMATIC_STREAK_STATUS.STOPPED ||
    streak.status === AUTOMATIC_STREAK_STATUS.CANCELLED
  ) {
    return null;
  }

  return new Date(streak.scheduledStartTime);
};

/**
 * Get the index of the failed cycle for V2 streaks
 */
const getFailedCycleIndex = (executionLog: StreakResponseV2["executionLog"]): number | null => {
  if (!executionLog?.scheduledCycles) return null;

  const failedIndex = executionLog.scheduledCycles.findIndex((cycle) => cycle.error);

  return failedIndex >= 0 ? failedIndex + 1 : null;
};

/**
 * Calculate the total skipped volume from V2 execution log
 */
const getSkippedVolume = (executionLog: StreakResponseV2["executionLog"]): number => {
  if (!executionLog?.scheduledCycles) return 0;

  return executionLog.scheduledCycles
    .filter((cycle) => cycle.isSkipped)
    .reduce((sum, cycle) => sum + cycle.targetVolume.toNumber(), 0);
};

/**
 * Get the last error from V2 execution log
 */
const getLastTradeError = (executionLog: StreakResponseV2["executionLog"]): string | null => {
  if (!executionLog?.scheduledCycles) return null;

  // Find the last cycle with an error
  const failedCycle = [...executionLog.scheduledCycles].reverse().find((cycle) => cycle.error);

  return failedCycle?.error ?? null;
};

interface AutomaticStreakProgressV2Props {
  streak: StreakResponseV2;
  onCancel?: () => void;
  isCancelling?: boolean;
  className?: string;
  /** When true, removes the container border/background (useful when rendered inside a modal) */
  bare?: boolean;
  /** Current epoch ID - used to show "Next Epoch" indicator */
  currentEpochId?: number;
  /** Spread threshold percentage from campaign config (default: 2%) */
  spreadThreshold?: number | null;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  [AUTOMATIC_STREAK_STATUS.PENDING]: <Loader2 className="w-5 h-5 animate-spin" />,
  [AUTOMATIC_STREAK_STATUS.IN_PROGRESS]: <Loader2 className="w-5 h-5 animate-spin" />,
  [AUTOMATIC_STREAK_STATUS.COMPLETED]: <CheckCircle className="w-5 h-5" />,
  [AUTOMATIC_STREAK_STATUS.STOPPED]: <Pause className="w-5 h-5" />,
  [AUTOMATIC_STREAK_STATUS.CANCELLED]: <Ban className="w-5 h-5" />,
};

const DEFAULT_ICON = <AlertTriangle className="w-5 h-5" />;

/**
 * Hook to create a countdown timer that updates every second
 * Returns seconds remaining until target time
 */
const useSecondsUntil = (targetTime: Date | null): number | null => {
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!targetTime) {
      setSecondsRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const now = Date.now();
      const target = targetTime.getTime();
      const diff = Math.max(0, Math.ceil((target - now) / 1000));
      setSecondsRemaining(diff);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [targetTime]);

  return secondsRemaining;
};

const DEFAULT_SPREAD_THRESHOLD = 2;

export const AutomaticStreakProgressV2 = ({
  streak,
  onCancel,
  isCancelling = false,
  className,
  bare = false,
  currentEpochId,
  spreadThreshold,
}: AutomaticStreakProgressV2Props) => {
  const displaySpreadThreshold = spreadThreshold ?? DEFAULT_SPREAD_THRESHOLD;
  const statusConfig = getStatusConfig(streak.status);
  const isNextEpoch = currentEpochId !== undefined && streak.epochId > currentEpochId;

  // Get total cycles from scheduledCycles
  const totalCycles = useMemo(() => {
    return streak.executionLog?.scheduledCycles?.length ?? 0;
  }, [streak.executionLog?.scheduledCycles]);

  // Progress calculation based on volume traded vs target volume
  const progress = useMemo(() => {
    const targetVolume = parseFloat(streak.totalTargetVolume);
    if (targetVolume === 0) return 0;
    const volumeTraded = parseFloat(streak.totalVolumeTraded);
    return Math.min(100, (volumeTraded / targetVolume) * 100);
  }, [streak.totalTargetVolume, streak.totalVolumeTraded]);

  // Calculate epoch start time (for "Next epoch starts in" countdown)
  const epochStartTime = useMemo(() => getEpochStartTime(streak), [streak]);
  const secondsUntilEpochStart = useSecondsUntil(epochStartTime);

  // Track if a cycle is currently being executed
  const executingCycle = useExecutingCycle(streak);

  const canCancel = isStreakCancellable(streak.status);
  const isActive = isStreakActive(streak.status);
  const statusIcon = STATUS_ICONS[streak.status] ?? DEFAULT_ICON;

  return (
    <div
      className={cn(bare ? "" : "p-4 rounded-lg border border-cede-border-container bg-cede-bg-component", className)}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-lg", statusConfig.bgColor, statusConfig.color)}>{statusIcon}</div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-cede-text-primary">{streak.pairSymbol}</h3>
              {isNextEpoch && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-cede-primary/20 text-cede-primary flex items-center gap-1">
                  <CalendarClock className="w-3 h-3" />
                  Next Epoch
                </span>
              )}
            </div>
            <span className={cn("text-sm", statusConfig.color)}>{statusConfig.label}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {executingCycle && (
            <span className="flex items-center gap-1 text-xs text-cede-primary">
              <Loader2 className="w-3 h-3 animate-spin" />
              Executing...
            </span>
          )}
          {canCancel && onCancel && (
            <Button variant="secondary" size="small" onClick={onCancel} disabled={isCancelling}>
              {isCancelling ? "Cancelling..." : "Cancel"}
            </Button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-cede-text-secondary flex items-center gap-1">
            Volume Progress
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]">
                <p>
                  Total volume to trade during the timeframe. The autopilot will execute buy + sell cycles to reach this
                  target.
                </p>
              </TooltipContent>
            </Tooltip>
          </span>
          <span className="text-cede-text-primary">
            ${parseFloat(streak.totalVolumeTraded).toFixed(2)}
            <span className="text-cede-text-tertiary"> / ${parseFloat(streak.totalTargetVolume).toFixed(2)}</span>
          </span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Next epoch countdown */}
      {isNextEpoch && secondsUntilEpochStart !== null && secondsUntilEpochStart > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-cede-primary" />
          <div>
            <p className="text-xs text-cede-text-secondary">Next epoch starts in</p>
            <p className="text-sm text-cede-primary font-medium">{formatCountdown(secondsUntilEpochStart)}</p>
          </div>
        </div>
      )}

      {/* Time Info & Configuration */}
      <div className="text-xs text-cede-text-tertiary space-y-1">
        {/* Timeframe */}
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-1">
            <Target className="w-3 h-3 text-cede-text-tertiary" />
            Timeframe:
          </span>
          <span>{formatTimeframe(streak.timeframeMs)}</span>
        </div>
        {streak.finishedAt && (
          <div className="flex justify-between">
            <span>Finished:</span>
            <span>{formatStreakDate(streak.finishedAt)}</span>
          </div>
        )}
        {/* Spread Protection Info */}
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-3 h-3 text-cede-success" />
            Spread protection:
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]">
                <p>
                  Cycles are automatically skipped when the bid-ask spread exceeds {displaySpreadThreshold}%. This
                  protects you from unfavorable trades during high volatility or low liquidity periods.
                </p>
              </TooltipContent>
            </Tooltip>
          </span>
          <span className="text-cede-success">{`<${displaySpreadThreshold}%`}</span>
        </div>
        {/* Volume Protection Info */}
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-3 h-3 text-cede-success" />
            Volume protection:
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]">
                <p>
                  Autopilot automatically stops when you reach your epoch volume limit. This prevents unnecessary trades
                  and protects you from paying extra fees.
                </p>
              </TooltipContent>
            </Tooltip>
          </span>
          <span className="text-cede-success">Active</span>
        </div>
        {/* Show skipped volume if any */}
        {(streak.skippedCycles ?? 0) > 0 && (
          <div className="flex justify-between items-center text-cede-warning">
            <span className="flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Volume skipped:
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3 h-3 text-cede-warning/70 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[250px]">
                  <p>
                    This volume was skipped because the bid-ask spread exceeded {displaySpreadThreshold}% at execution
                    time. This protects you from unfavorable trades.
                  </p>
                </TooltipContent>
              </Tooltip>
            </span>
            <span>${getSkippedVolume(streak.executionLog).toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Stop Reason */}
      {streak.stopReason && (
        <div className="mt-4 p-3 rounded-lg bg-cede-error/10 border border-cede-error/20">
          <p className="text-sm text-cede-error flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>
              {getStopReasonLabel(streak.stopReason)}
              {/* Show which trade failed */}
              {getFailedCycleIndex(streak.executionLog) && (
                <span className="text-cede-error/70">
                  {" "}
                  on cycle {getFailedCycleIndex(streak.executionLog)}/{totalCycles}
                </span>
              )}
            </span>
          </p>
          {/* Show actual trade error message if available */}
          {getLastTradeError(streak.executionLog) && (
            <p className="text-xs text-cede-error/80 mt-1.5 ml-6 italic">
              &ldquo;{getLastTradeError(streak.executionLog)}&rdquo;
            </p>
          )}
        </div>
      )}

      {/* Active Indicator */}
      {isActive && (
        <div className="mt-4 flex items-center gap-2 text-xs text-cede-text-secondary">
          <div className="w-2 h-2 rounded-full bg-cede-success animate-pulse" />
          <span>Auto-updating</span>
        </div>
      )}
    </div>
  );
};
